import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import leadActivityService from '../../../api/crm/leads/leadActivityService';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import { toast } from '../../../utils/toast';
import { formatDate } from '../../../utils/stringUtils';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Linkedin,
  User,
  Building,
  X,
  Paperclip
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE;
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === 'true';

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!id) {
        setError('Activity ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await leadActivityService.getActivityById(id);
        if (result.success) {
          setActivity(result.activity);
        } else {
          setError(result.error || 'Failed to load activity');
          toast.error(result.error || 'Failed to load activity');
        }
      } catch (error) {

        setError('An error occurred while loading activity');
        toast.error('An error occurred while loading activity');
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [id]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return Phone;
      case 'email': return Mail;
      case 'whatsapp': return MessageSquare;
      case 'meeting': return Calendar;
      case 'linkedin': return Linkedin;
      default: return FileText;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'call': return 'text-[#0F2942]';
      case 'email': return 'text-green-600';
      case 'whatsapp': return 'text-green-500';
      case 'meeting': return 'text-purple-600';
      case 'linkedin': return 'text-sky-700';
      default: return 'text-gray-600';
    }
  };



  const getLeadName = (lead) => {
    if (!lead) return 'Unknown Lead';
    if (typeof lead === 'string') return lead;
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.email) {
      return lead.email;
    }
    return 'Unknown Lead';
  };

  const getPerformerName = (performedBy) => {
    if (!performedBy) return 'Unknown User';
    if (typeof performedBy === 'string') return performedBy;
    if (performedBy.firstName && performedBy.lastName) {
      return `${performedBy.firstName} ${performedBy.lastName}`;
    } else if (performedBy.firstName) {
      return performedBy.firstName;
    } else if (performedBy.email) {
      return performedBy.email;
    }
    return 'Unknown User';
  };

  const handleDownloadAttachment = async (e, att) => {
    e.preventDefault();
    e.stopPropagation();
    let loader;
    try {
      const publicId = att.public_id || att.publicId;
      if (!publicId) {
        toast.error("File details not available for download");
        return;
      }
      
      loader = toast.info("Preparing download...", 30000);
      const fileName = att.fileName || att.filename || 'file';
      const token = localStorage.getItem('auth_token');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const downloadUrl = `${apiBaseUrl}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(fileName)}`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

      if (loader && typeof loader.remove === 'function') {
        loader.remove();
      }
      toast.success("Download started!");
    } catch (error) {
      if (loader && typeof loader.remove === 'function') {
        loader.remove();
      }
      console.error('Error downloading file:', error);
      toast.error('Failed to download file.');
    }
  };


  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  // Helper function to map RP Digital call status to UI display text
  const getCallStatusDisplay = (status) => {
    if (!status) return null;
    const statusUpper = status.toUpperCase();
    const statusMap = {
      'ANSWER': 'Answered',
      'PICKED': 'Answered', // Picked means call was answered
      'NOANSWER': 'Missed / No Answer',
      'BUSY': 'Busy',
      'CANCEL-AGENT': 'Cancelled by Agent',
      'CANCEL-CUSTOMER': 'Cancelled by Customer',
      'FAILED': 'Failed',
      'PENDING': 'Pending'
    };
    return statusMap[statusUpper] || status;
  };

  // Helper function to get call status badge color
  const getCallStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case 'ANSWER': return 'bg-green-100 text-green-800';
      case 'PICKED': return 'bg-green-100 text-green-800'; // Picked means answered
      case 'NOANSWER': return 'bg-yellow-100 text-yellow-800';
      case 'BUSY': return 'bg-orange-100 text-orange-800';
      case 'CANCEL-AGENT': return 'bg-red-100 text-red-800';
      case 'CANCEL-CUSTOMER': return 'bg-red-100 text-red-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get the correct activity time
  // Priority: performedAt (when call was initiated) > callMetadata times > createdAt
  // This ensures consistent time display before and after webhook updates
  const getActivityTime = (activity) => {
    // Always prioritize performedAt first - this is when the call was actually initiated
    if (activity.performedAt) {
      return new Date(activity.performedAt).toISOString();
    }

    // For automated calls, fallback to callMetadata times if performedAt is not available
    if (activity.isAutomated && activity.callMetadata) {
      // Priority order: startTime > legBStartTime > legAPickedTime > legBPickedTime
      const callTime = activity.callMetadata.startTime ||
        activity.callMetadata.legBStartTime ||
        activity.callMetadata.legAPickedTime ||
        activity.callMetadata.legBPickedTime;

      if (callTime) {
        try {
          const date = new Date(callTime);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        } catch (error) {

        }
      }
    }

    // Final fallback to createdAt
    return activity.createdAt || new Date().toISOString();
  };

  const formatDuration = (minutes, callDurationSeconds = null) => {
    // If callDurationSeconds is provided (from callMetadata), use exact seconds
    if (callDurationSeconds !== null && callDurationSeconds !== undefined) {
      const totalSeconds = Math.floor(callDurationSeconds);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hours > 0) {
        if (mins === 0 && secs === 0) {
          return `${hours} hour${hours !== 1 ? 's' : ''}`;
        } else if (secs === 0) {
          return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
        } else {
          return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
        }
      } else if (mins > 0) {
        if (secs === 0) {
          return `${mins} minute${mins !== 1 ? 's' : ''}`;
        } else {
          return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
        }
      } else {
        return `${secs} second${secs !== 1 ? 's' : ''}`;
      }
    }

    // Fallback to minutes (for non-automated calls or if callDuration not available)
    if (!minutes) return '';

    // Convert minutes to exact seconds for precise display
    const totalSeconds = Math.floor(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else if (mins > 0) {
      if (secs === 0) {
        return `${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
  };

  const handleDelete = async () => {


    // Validate activity first
    if (!activity || !activity._id) {

      toast.error('Activity information is missing');
      return;
    }

    // Show shorter confirmation dialog - THIS MUST BE CALLED FIRST
    const confirmMessage = `Delete activity "${activity.title}"?\n\nThis action cannot be undone.`;


    // CRITICAL: Show confirmation dialog FIRST - handle both sync and async cases
    // Some environments may return a Promise, so we handle both
    let confirmed;
    const confirmResult = window.confirm(confirmMessage);

    // Check if it's a Promise (async) or boolean (sync)
    if (confirmResult instanceof Promise) {

    } else {
      confirmed = confirmResult;
    }



    // If user clicked Cancel, stop here - do NOT proceed
    if (confirmed !== true && confirmed !== 'true') {

      return; // User cancelled, exit immediately
    }

    // ONLY if user clicked OK (confirmed === true), proceed with delete
    // This code only runs AFTER user confirms


    try {

      const result = await leadActivityService.deleteActivity(activity._id);


      if (result.success) {

        toast.success('Activity deleted successfully');

        navigate('/lead_activities');
      } else {

        toast.error(result.error || 'Failed to delete activity');
      }
    } catch (error) {

      toast.error('An error occurred while deleting the activity');
    }
  };

  const handleEdit = () => {
    // Navigate to LogActivity page in edit mode with the activity's lead ID
    if (activity.lead && (activity.lead._id || activity.lead.id)) {
      const leadId = activity.lead._id || activity.lead.id;
      navigate(`/log_activity/${leadId}?edit=true&activityId=${id}`);
    } else {
      toast.error('Cannot edit: Lead information not available');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading activity details...</p>
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Activity</h3>
          <p className="text-gray-600 mb-4">{error || 'Activity not found'}</p>
          <Button
            variant="primary"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back to Activities
          </Button>
        </div>
      </div>
    );
  }

  const IconComponent = getActivityIcon(activity.type);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center space-x-4 min-w-0 flex-1">
            <Button
              variant="outline"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="p-2 rounded-lg bg-gray-100 flex-shrink-0">
                <IconComponent className={`h-6 w-6 ${getActivityColor(activity.type)}`} />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-gray-900 break-words first-letter:capitalize">{activity.title}</h1>
                <p className="text-gray-600 capitalize">{activity.type} Activity</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3 flex-shrink-0">
            {!isSalesExecutive && (
              <Button
                variant="outline"
                icon={<Edit className="h-4 w-4" />}
                onClick={handleEdit}
                className="hover:bg-green-50 hover:border-green-300 hover:text-green-700"
              >
                Edit
              </Button>
            )}
            {!isSalesExecutive && (
              <Button
                variant="outline"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete();
                }}
                className="hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                type="button"
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Activity Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                {activity.lead && (
                  <>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-20">Lead:</span>
                        <span className="text-gray-900 font-medium break-words flex-1 min-w-0 capitalize">
                          {getLeadName(activity.lead)}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-20">Company:</span>
                        <span className="text-gray-900 break-words flex-1 min-w-0 first-letter:capitalize">
                          {activity.lead.company || 'No company'}
                        </span>
                      </div>
                    </div>
                    {activity.lead.email && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-4">
                          <span className="text-gray-600 flex-shrink-0 w-20">Email:</span>
                          <span className="text-gray-900 break-all flex-1 min-w-0 lowercase">
                            {activity.lead.email}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 flex-shrink-0 w-28">Date:</span>
                    <span className="text-gray-900 flex-shrink-0">
                      {formatDateOnly(getActivityTime(activity))}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 flex-shrink-0 w-28">Time:</span>
                    <span className="text-gray-900 flex-shrink-0">
                      {formatTimeOnly(getActivityTime(activity))}
                    </span>
                  </div>
                </div>
                {(() => {
                  // For meetings, show duration if available
                  if (activity.type === 'meeting' && activity.duration) {
                    return (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <span className="text-gray-600 flex-shrink-0 w-28">Duration:</span>
                          <span className="text-gray-900 flex-shrink-0">{formatDuration(activity.duration)}</span>
                        </div>
                      </div>
                    );
                  }

                  // For calls, ONLY show actual talk time (talkDuration) - no fallbacks
                  // This means only answered/picked calls that had actual conversation will show duration
                  const talkDuration = activity.callMetadata?.talkDuration;

                  // Only show if we have actual talk time - no fallbacks
                  return talkDuration !== null && talkDuration !== undefined && talkDuration > 0 ? (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <span className="text-gray-600 flex-shrink-0 w-28">Duration:</span>
                        <span className="text-gray-900 flex-shrink-0">{formatDuration(null, talkDuration)}</span>
                      </div>
                    </div>
                  ) : null;
                })()}
                {activity.type === 'call' && activity.callMetadata?.status && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 flex-shrink-0 w-28">Call Status:</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCallStatusColor(activity.callMetadata.status)}`}>
                        {getCallStatusDisplay(activity.callMetadata.status)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-2">Description</h4>
            <p className="text-gray-700 bg-gray-50 p-3 rounded-lg break-words whitespace-pre-wrap first-letter:capitalize">{activity.description || 'No description provided'}</p>
          </div>

          {/* Attachments */}
          {activity.attachments && activity.attachments.length > 0 && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Attachments</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {activity.attachments.map((att, attIdx) => (
                  <div key={attIdx} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-xl">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-brand/10 text-brand rounded-lg flex-shrink-0">
                        <Paperclip className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate" title={att.fileName}>
                          {att.fileName}
                        </p>
                        <button
                          type="button"
                          onClick={(e) => handleDownloadAttachment(e, att)}
                          className="text-xs text-brand hover:underline font-semibold text-left focus:outline-none"
                        >
                          View/Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* Performed By Section */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-2">Performed By</h4>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              {activity.performedBy ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Name</span>
                    <span className="text-sm font-semibold text-gray-955 capitalize">
                      {getPerformerName(activity.performedBy)}
                    </span>
                  </div>
                  {activity.performedBy.employeeId && (
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Employee ID</span>
                      <span className="text-sm text-gray-800 font-mono">
                        {activity.performedBy.employeeId}
                      </span>
                    </div>
                  )}
                  {activity.performedBy.email && (
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Email</span>
                      <span className="text-sm text-gray-800 lowercase break-all">
                        {activity.performedBy.email}
                      </span>
                    </div>
                  )}
                  {activity.performedBy.role && (
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Role</span>
                      <span className="text-sm text-gray-800 capitalize">
                        {String(activity.performedBy.role).replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Timestamp</span>
                    <span className="text-sm text-gray-800">
                      {formatDate(activity.performedAt)}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="text-sm text-gray-400">No performer details available</span>
              )}
            </div>
          </div>

          {/* Updated By Section */}
          {activity.updatedBy && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Updated By</h4>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Name</span>
                    <span className="text-sm font-semibold text-gray-955 capitalize font-medium">
                      {getPerformerName(activity.updatedBy)}
                    </span>
                  </div>
                  {activity.updatedBy.employeeId && (
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Employee ID</span>
                      <span className="text-sm text-gray-800 font-mono">
                        {activity.updatedBy.employeeId}
                      </span>
                    </div>
                  )}
                  {activity.updatedBy.email && (
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Email</span>
                      <span className="text-sm text-gray-800 lowercase break-all">
                        {activity.updatedBy.email}
                      </span>
                    </div>
                  )}
                  {activity.updatedBy.role && (
                    <div>
                      <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Role</span>
                      <span className="text-sm text-gray-800 capitalize">
                        {String(activity.updatedBy.role).replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="block text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Timestamp</span>
                    <span className="text-sm text-gray-800">
                      {formatDate(activity.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}



          {/* Notes */}
          {activity.notes && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-2">Notes</h4>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg break-words whitespace-pre-wrap italic">{activity.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default ActivityDetail;
